import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  trackPageView,
  trackScroll,
  trackTimeOnPage,
  trackActiveTime,
  trackReadingProgress,
  trackSectionView,
  trackClick,
} from "@/lib/analytics";

export const usePageTracking = () => {
  const location = useLocation();
  const startTime = useRef(0);
  const activeTime = useRef(0);
  const isActive = useRef(true);
  const activeInterval = useRef(null);
  const maxScroll = useRef(0);
  const scrollThresholds = useRef(new Set());

  useEffect(() => {
    trackPageView(location.pathname, document.title);
    startTime.current = Date.now();
    activeTime.current = 0;
    maxScroll.current = 0;
    scrollThresholds.current = new Set();
    isActive.current = true;

    activeInterval.current = setInterval(() => {
      if (isActive.current) {
        activeTime.current += 1;
      }
    }, 1000);

    const onVisChange = () => {
      isActive.current = !document.hidden;
    };
    const onFocus = () => {
      isActive.current = true;
    };
    const onBlur = () => {
      isActive.current = false;
    };

    document.addEventListener("visibilitychange", onVisChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      const totalTime = (Date.now() - startTime.current) / 1000;
      trackTimeOnPage(totalTime, location.pathname);
      trackActiveTime(activeTime.current, location.pathname);
      clearInterval(activeInterval.current);
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, [location.pathname]);

  return null;
};

export const useScrollTracking = (scrollRef, pageName) => {
  const thresholds = useRef(new Set());

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return;

    thresholds.current = new Set();

    const onScroll = () => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);

      [25, 50, 75, 90, 100].forEach((t) => {
        if (pct >= t && !thresholds.current.has(t)) {
          thresholds.current.add(t);
          trackScroll(t, pageName);
        }
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, pageName]);
};

export const useReadingTracker = (scrollRef, articleId, articleTitle) => {
  const thresholds = useRef(new Set());

  useEffect(() => {
    if (!articleId) return;
    const el = scrollRef?.current;
    if (!el) return;

    thresholds.current = new Set();

    const onScroll = () => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);

      [10, 25, 50, 75, 100].forEach((t) => {
        if (pct >= t && !thresholds.current.has(t)) {
          thresholds.current.add(t);
          trackReadingProgress(articleId, articleTitle, t);
        }
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, articleId, articleTitle]);
};

export const useSectionObserver = (articleId) => {
  const observedSections = useRef(new Set());

  const observeSection = useCallback(
    (element, sectionName) => {
      if (!element || !articleId) return;
      const key = `${articleId}-${sectionName}`;
      if (observedSections.current.has(key)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !observedSections.current.has(key)) {
              observedSections.current.add(key);
              trackSectionView(articleId, sectionName);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 },
      );

      observer.observe(element);
      return () => observer.disconnect();
    },
    [articleId],
  );

  return observeSection;
};

export const useClickTracking = () => {
  useEffect(() => {
    const handler = (e) => {
      const btn = e.target.closest("button, a, [role='button']");
      if (!btn) return;

      const label =
        btn.getAttribute("aria-label") ||
        btn.getAttribute("data-track") ||
        btn.textContent?.trim().slice(0, 50) ||
        "unknown";

      const href = btn.getAttribute("href");
      const isExternal = href && href.startsWith("http");

      trackClick(label, isExternal ? "outbound" : "engagement", {
        element_type: btn.tagName.toLowerCase(),
        page_path: window.location.pathname,
        ...(href ? { link_url: href } : {}),
      });
    };

    document.addEventListener("click", handler, { capture: true });
    return () =>
      document.removeEventListener("click", handler, { capture: true });
  }, []);
};
